import React, { useMemo, useState } from 'react';
import { ArrowRight, Check, CircleAlert, ClipboardCheck, FileCheck2, ShieldCheck } from 'lucide-react';
import SEO from '@/components/seo/SEO';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { getSignupUrl } from '@/lib/appLinks';
import { trackSignupIntent, trackToolUsage } from '@/lib/tracking';
import { cn } from '@/lib/utils';

type DemoStatus = 'unchecked' | 'pass' | 'maintenance_required' | 'na';

interface DemoFinding {
  bmpName: string;
  prompt: string;
  status: DemoStatus;
  notes: string;
}

const DRAFT_KEY = 'runofftrack.inspection_draft';

const initialFindings: DemoFinding[] = [
  {
    bmpName: 'Silt Fence',
    prompt: 'Is the fence upright, entrenched, and free of damaging sediment buildup?',
    status: 'unchecked',
    notes: ''
  },
  {
    bmpName: 'Stabilized Construction Entrance',
    prompt: 'Is the entrance limiting track-out without excessive sediment accumulation?',
    status: 'unchecked',
    notes: ''
  },
  {
    bmpName: 'Concrete Washout',
    prompt: 'Is the washout contained, labeled, accessible, and below capacity?',
    status: 'unchecked',
    notes: ''
  }
];

const statusOptions: Array<{ value: Exclude<DemoStatus, 'unchecked'>; label: string }> = [
  { value: 'pass', label: 'Acceptable' },
  { value: 'maintenance_required', label: 'Needs attention' },
  { value: 'na', label: 'Not present' }
];

const SampleInspectionPage: React.FC = () => {
  const [findings, setFindings] = useState(initialFindings);
  const [generalNotes, setGeneralNotes] = useState('');

  const reviewedCount = findings.filter((finding) => finding.status !== 'unchecked').length;
  const attentionCount = findings.filter((finding) => finding.status === 'maintenance_required').length;
  const isReady = reviewedCount === findings.length;

  const resultLabel = useMemo(() => {
    if (!isReady) return `${reviewedCount} of ${findings.length} controls reviewed`;
    if (attentionCount > 0) return `${attentionCount} corrective ${attentionCount === 1 ? 'item' : 'items'} identified`;
    return 'Sample review complete';
  }, [attentionCount, findings.length, isReady, reviewedCount]);

  const updateFinding = (index: number, update: Partial<DemoFinding>) => {
    setFindings((current) => current.map((finding, findingIndex) => (
      findingIndex === index ? { ...finding, ...update } : finding
    )));
  };

  const continueToApp = () => {
    if (!isReady) return;
    const draft = {
      version: 1,
      source: 'sample_inspection',
      createdAt: new Date().toISOString(),
      generalNotes,
      findings: findings.map((finding, index) => ({
        id: `sample-${index}`,
        bmpName: finding.bmpName,
        status: finding.status,
        notes: finding.notes,
        photos: []
      }))
    };
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // Signup still works if browser storage is unavailable; only draft import is skipped.
    }
    trackToolUsage('sample_inspection', 'complete', {
      attention_count: attentionCount,
      reviewed_count: reviewedCount
    });
    trackSignupIntent('sample_inspection');
    window.location.assign(getSignupUrl(undefined, '/sites?onboarding=inspection'));
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Sample SWPPP Inspection',
    description: 'Try a short construction stormwater inspection workflow before creating an account.',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }
  };

  return (
    <div className="page-shell">
      <SEO
        title="Try a Sample SWPPP Inspection"
        description="Review three common stormwater controls in a guided sample inspection, then save the work as your first RunOffTrack project."
        canonical="/demo/sample-inspection"
        jsonLd={jsonLd}
      />

      <section className="page-intro border-b border-border/70">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <Breadcrumbs items={[
            { label: 'Sample Inspection', href: '/demo/sample-inspection' }
          ]} />
          <div className="mx-auto mb-6 mt-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <ClipboardCheck className="h-7 w-7" />
          </div>
          <p className="section-label mb-3">No account required</p>
          <h1 className="text-4xl font-black tracking-[-0.04em] md:text-6xl">Try the inspection workflow.</h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Review three common controls. Your choices can follow you into a free project if you decide to continue.
          </p>
        </div>
      </section>

      <section className="container mx-auto grid max-w-6xl gap-8 px-4 py-12 lg:grid-cols-[1fr_320px] lg:py-16">
        <div className="space-y-5">
          {findings.map((finding, index) => (
            <Card key={finding.bmpName} className={cn(
              'overflow-hidden transition-colors',
              finding.status === 'maintenance_required' && 'border-orange-500/60',
              finding.status === 'pass' && 'border-primary/40'
            )}>
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="section-label mb-2">Control {index + 1}</p>
                    <CardTitle>{finding.bmpName}</CardTitle>
                  </div>
                  <div className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border',
                    finding.status === 'pass' && 'border-primary bg-primary text-primary-foreground',
                    finding.status === 'maintenance_required' && 'border-orange-500 bg-orange-500 text-white',
                    (finding.status === 'unchecked' || finding.status === 'na') && 'border-border bg-secondary text-muted-foreground'
                  )}>
                    {finding.status === 'pass' ? <Check className="h-5 w-5" /> : finding.status === 'maintenance_required' ? <CircleAlert className="h-5 w-5" /> : <span className="text-sm font-bold">{index + 1}</span>}
                  </div>
                </div>
                <p className="pt-2 text-sm leading-relaxed text-muted-foreground">{finding.prompt}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="group" aria-label={`${finding.bmpName} status`}>
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={finding.status === option.value}
                      onClick={() => updateFinding(index, { status: option.value })}
                      className={cn(
                        'min-h-11 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        finding.status === option.value
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background hover:border-primary/50 hover:bg-secondary/60'
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {finding.status === 'maintenance_required' && (
                  <Textarea
                    value={finding.notes}
                    onChange={(event) => updateFinding(index, { notes: event.target.value })}
                    placeholder="What needs correction?"
                    aria-label={`${finding.bmpName} corrective action notes`}
                  />
                )}
              </CardContent>
            </Card>
          ))}

          <Card>
            <CardHeader><CardTitle className="text-lg">General site notes</CardTitle></CardHeader>
            <CardContent>
              <Textarea
                value={generalNotes}
                onChange={(event) => setGeneralNotes(event.target.value)}
                placeholder="Optional observations about access, weather, or site conditions…"
                aria-label="General site notes"
              />
            </CardContent>
          </Card>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Card className="overflow-hidden border-primary/25 shadow-lg">
            <CardHeader className="border-b border-border bg-secondary/45">
              <p className="section-label">Inspection progress</p>
              <CardTitle className="mt-2 text-xl">{resultLabel}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              <div className="h-2 overflow-hidden rounded-full bg-secondary">
                <div className="h-full bg-primary transition-all" style={{ width: `${(reviewedCount / findings.length) * 100}%` }} />
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex gap-2"><ShieldCheck className="h-4 w-4 shrink-0 text-primary" /> Sample data stays in this browser.</li>
                <li className="flex gap-2"><FileCheck2 className="h-4 w-4 shrink-0 text-primary" /> Continue into a real project without re-entering these findings.</li>
              </ul>
              <Button size="lg" className="w-full" disabled={!isReady} onClick={continueToApp}>
                Save as my first inspection <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              {!isReady && <p className="text-center text-xs text-muted-foreground">Review all three controls to continue.</p>}
              <p className="text-xs leading-relaxed text-muted-foreground">
                This demonstration is not a compliance determination. Apply the requirements of your project permit and permitting authority.
              </p>
            </CardContent>
          </Card>
        </aside>
      </section>
    </div>
  );
};

export default SampleInspectionPage;
